/**
 * Änderungen im Fragebogen markieren – Kundenfeedback Kanzlei Polz
 * 24.10.2026 (IV.10): „Bei Änderungen im Fragebogen vom Mdt/AH/SV etc. wäre
 * es gut, wenn es die vorgenommenen Änderungen irgendwie markiert, dass man
 * sieht, was geändert wurde."
 *
 * GET /protected/cases/{id}/questionnaire/changes liefert die Felder, die seit
 * der von der eigenen Organisation zuletzt gesehenen Fragebogen-Version von
 * anderen geändert wurden (backend/internal/fall/qchanges). Hier liegen die
 * reinen Hilfsfunktionen für Markierung, Tooltip und Zusammenfassung.
 */

export interface QuestionnaireChangeAuthor {
  idUser: number;
  name: string;
  organization: string;
  isCustomer: boolean;
}

export interface QuestionnaireChange {
  key: string;
  section: string;
  sectionTitle: string;
  index: number;
  field: string;
  label: string;
  groupId: string;
  groupTitle: string;
  table: string;
  idRow: number;
  before: string;
  after: string;
  versionId: number;
  changedAt: string;
  changedBy: QuestionnaireChangeAuthor | null;
}

export interface QuestionnaireChangeAuthorSummary extends QuestionnaireChangeAuthor {
  count: number;
  lastChangedAt: string;
}

export interface QuestionnaireChangesResult {
  versionCount: number;
  latestVersionId: number;
  baselineId: number;
  baselineAt: string | null;
  seenAt: string | null;
  changes: QuestionnaireChange[];
  authors: QuestionnaireChangeAuthorSummary[];
}

type Translate = (text: string) => string;
const identity: Translate = (text) => text;

/** Abschnitt des Fragebogens (PDF-Abschnitt) → Schritt im Cockpit-Fragebogen (componentKey). */
const STEP_BY_SECTION: Readonly<Record<string, string>> = {
  clients: "client",
  vehicles: "vehicle",
  opponents: "opponent",
  witnessesMaster: "witnesses",
  witnesses: "witnesses",
  accident: "accident",
  policeProsecution: "policeProsecution",
  settlement: "settlement",
  remarks: "remarks",
};

export function stepKeyForSection(section: string): string | null {
  return STEP_BY_SECTION[section] ?? null;
}

/** Schlüssel einer Markierung: Tabelle, Zeile, Feld – wie FieldLabel sie kennt. */
export function changeCellKey(table: string, idRow: number | string | null | undefined, field: string): string {
  return `${table}#${Number(idRow) || 0}#${field}`;
}

/** Änderungen je Feld nachschlagbar machen. */
export function indexChanges(changes: readonly QuestionnaireChange[]): Map<string, QuestionnaireChange> {
  const map = new Map<string, QuestionnaireChange>();
  for (const change of changes) {
    if (!change.idRow) continue; // gelöschter Eintrag: nur in der Liste
    map.set(changeCellKey(change.table, change.idRow, change.field), change);
  }
  return map;
}

export function formatChangeDate(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatChangeDay(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** „Jasmin Fischer (Autohaus Zwerger)", „Max Muster (Mandant)", „Unbekannt". */
export function authorLabel(author: QuestionnaireChangeAuthor | null | undefined, gt: Translate = identity): string {
  if (!author) return gt("Unbekannt");
  const org = author.isCustomer ? gt("Mandant") : author.organization;
  const name = author.name || "";
  if (name && org && name !== org) return `${name} (${org})`;
  return name || org || gt("Unbekannt");
}

/** Wer – in der Zusammenfassung: Organisation bzw. „Mandant", sonst der Name. */
function authorOrganization(author: QuestionnaireChangeAuthor | null | undefined, gt: Translate): string {
  if (!author) return gt("Unbekannt");
  if (author.isCustomer) return author.name ? `${gt("Mandant")} ${author.name}` : gt("Mandant");
  return author.organization || author.name || gt("Unbekannt");
}

function shownValue(value: string, gt: Translate): string {
  return value && value.trim() ? value : gt("leer");
}

/** Tooltip am Feld: „Geändert von Jasmin Fischer (Autohaus Zwerger) am 24.10.2026, 14:03 – vorher: Maier". */
export function changeTooltip(change: QuestionnaireChange, gt: Translate = identity): string {
  const when = formatChangeDate(change.changedAt);
  const who = authorLabel(change.changedBy, gt);
  return `${gt("Geändert von")} ${who}${when ? ` ${gt("am")} ${when}` : ""} – ${gt("vorher")}: ${shownValue(change.before, gt)}`;
}

/** Zeilen der Zusammenfassung: „7 Änderungen durch Autohaus Zwerger am 24.10.2026". */
export function summaryLines(result: QuestionnaireChangesResult | null | undefined, gt: Translate = identity): string[] {
  if (!result?.authors?.length) return [];
  return result.authors.map((a) => {
    const noun = a.count === 1 ? gt("Änderung") : gt("Änderungen");
    const day = formatChangeDay(a.lastChangedAt);
    const person = !a.isCustomer && a.name && a.organization && a.name !== a.organization ? ` (${a.name})` : "";
    return `${a.count} ${noun} ${gt("durch")} ${authorOrganization(a, gt)}${person}${day ? ` ${gt("am")} ${day}` : ""}`;
  });
}

/** Zeile der Änderungsliste: „Kunde 1 › Nachname". */
export function changeLocation(change: QuestionnaireChange, gt: Translate = identity): string {
  const multi = ["clients", "vehicles", "opponents", "witnesses", "settlement"].includes(change.section);
  const section = multi ? `${gt(change.sectionTitle)} ${change.index + 1}` : gt(change.sectionTitle);
  return `${section} › ${gt(change.label || change.field)}`;
}

/** Vorher → jetzt, für die Änderungsliste. */
export function changeValues(change: QuestionnaireChange, gt: Translate = identity): string {
  return `${shownValue(change.before, gt)} → ${shownValue(change.after, gt)}`;
}
